import { BlogApi, BlogPost, BlogCategory, BlogPostResponse, BlogCategoryResponse, BlogPostDetailResponse, BlogCategoryDetailResponse, AdminBlogPostResponse, AdminBlogCategoryResponse, AdminBlogPostDetailResponse, AdminBlogCategoryDetailResponse } from '../../Api/Modules/Blog';

export type { BlogPost, BlogCategory } from '../../Api/Modules/Blog';

export class BlogService {
  private static instance: BlogService;
  private blogApi: BlogApi;

  private constructor() {
    this.blogApi = BlogApi.getInstance();
  }

  public static getInstance(): BlogService {
    if (!BlogService.instance) {
      BlogService.instance = new BlogService();
    }
    return BlogService.instance;
  }

  // Admin methods (protected)
  async getBlogPosts(): Promise<BlogPost[]> {
    try {
      const response = await this.blogApi.getAdminBlogPosts();
      const posts = response?.data || [];
      
      // API response'unda her post'un category objesi var, bunu category_id olarak dönüştür
      return posts.map(post => {
        if ((post as any).category) {
          return {
            ...post,
            category_id: (post as any).category.id
          };
        }
        return post;
      });
    } catch (error) {
      console.error('❌ BlogService: getBlogPosts failed', error);
      throw error;
    }
  }

  async getBlogPost(id: string): Promise<BlogPost> {
    try {
      const response = await this.blogApi.getAdminBlogPost(id);
      const post = response?.data;
      
      // API response'unda category objesi var, bunu category_id olarak dönüştür
      if (post && (post as any).category) {
        return {
          ...post,
          category_id: (post as any).category.id
        };
      }
      
      return post;
    } catch (error) {
      console.error('❌ BlogService: getBlogPost failed', error);
      throw error;
    }
  }

  async createBlogPost(post: Omit<BlogPost, 'id'>): Promise<BlogPost> {
    try {
      const response = await this.blogApi.createBlogPost(post);
      return response?.data;
    } catch (error) {
      console.error('❌ BlogService: createBlogPost failed', error);
      throw error;
    }
  }

  async updateBlogPost(id: string, post: Partial<BlogPost>): Promise<BlogPost> {
    try {
      const response = await this.blogApi.updateBlogPost(id, post);
      return response?.data;
    } catch (error) {
      console.error('❌ BlogService: updateBlogPost failed', error);
      throw error;
    }
  }

  async deleteBlogPost(id: string): Promise<void> {
    try {
      await this.blogApi.deleteBlogPost(id);
    } catch (error) {
      console.error('❌ BlogService: deleteBlogPost failed', error);
      throw error;
    }
  }

  async getCategories(): Promise<BlogCategory[]> {
    try {
      const response = await this.blogApi.getAdminCategories();
      return response?.data || [];
    } catch (error) {
      console.error('❌ BlogService: getCategories failed', error);
      throw error;
    }
  }

  async getCategory(id: string): Promise<BlogCategory> {
    try {
      const response = await this.blogApi.getAdminCategory(id);
      return response?.data;
    } catch (error) {
      console.error('❌ BlogService: getCategory failed', error);
      throw error;
    }
  }

  async createCategory(category: Omit<BlogCategory, 'id'>): Promise<BlogCategory> {
    try {
      const response = await this.blogApi.createCategory(category);
      return response?.data;
    } catch (error) {
      console.error('❌ BlogService: createCategory failed', error);
      throw error;
    }
  }

  async updateCategory(id: string, category: Partial<BlogCategory>): Promise<BlogCategory> {
    try {
      const response = await this.blogApi.updateCategory(id, category);
      return response?.data;
    } catch (error) {
      console.error('❌ BlogService: updateCategory failed', error);
      throw error;
    }
  }

  async deleteCategory(id: string): Promise<void> {
    try {
      await this.blogApi.deleteCategory(id);
    } catch (error) {
      console.error('❌ BlogService: deleteCategory failed', error);
      throw error;
    }
  }

  // Public methods (unprotected)
  async getPublicBlogPosts(params?: {
    page?: number;
    limit?: number;
    category_id?: string;
    search?: string;
  }): Promise<BlogPostResponse> {
    try {
      return await this.blogApi.getPublicBlogPosts(params);
    } catch (error) {
      console.error('❌ BlogService: getPublicBlogPosts failed', error);
      throw error;
    }
  }

  async getPublicBlogPost(slug: string): Promise<BlogPostDetailResponse> {
    try {
      return await this.blogApi.getPublicBlogPost(slug);
    } catch (error) {
      console.error('❌ BlogService: getPublicBlogPost failed', error);
      throw error;
    }
  }

  async getPublicCategories(): Promise<BlogCategoryResponse> {
    try {
      return await this.blogApi.getPublicCategories();
    } catch (error) {
      console.error('❌ BlogService: getPublicCategories failed', error);
      throw error;
    }
  }

  async getPublicCategory(slug: string): Promise<BlogCategoryDetailResponse> {
    try {
      return await this.blogApi.getPublicCategory(slug);
    } catch (error) {
      console.error('❌ BlogService: getPublicCategory failed', error);
      throw error;
    }
  }

  // Utility methods
  generateSlug(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }
} 